/**
 * Application status helpers — single source of truth for client-facing
 * status labels across the dashboard (Sidebar, Home, Portfolio, Payments).
 *
 * Full status enum (see "Free Report Funnel" implementation plan):
 *   active → quote_requested → quote_sent → (paid) → pending_attorney_filing
 *     → filing_in_progress → filed → registered
 *   plus: completed, cancelled, refunded, partially_refunded
 */

// status → [i18n key (dashboard namespace), English fallback]
const STATUS_LABELS = {
  active: ['status.inProgress', 'In Progress'],
  draft: ['status.inProgress', 'In Progress'],
  quote_requested: ['status.quoteRequested', 'Preparing your report'],
  quote_sent: ['status.quoteSent', 'Quote ready'],
  payment_pending: ['status.paymentPending', 'Payment Pending'],
  payment_completed: ['status.paymentComplete', 'Payment Complete'],
  pending_attorney_filing: ['status.pendingFiling', 'Pending Filing'],
  filing_in_progress: ['status.filingInProgress', 'Filing in Progress'],
  filed: ['status.filed', 'Filed'],
  registered: ['status.registered', 'Registered'],
  completed: ['status.completed', 'Completed'],
  cancelled: ['status.cancelled', 'Cancelled'],
  refunded: ['status.refunded', 'Refunded'],
  partially_refunded: ['status.partiallyRefunded', 'Partially Refunded'],
};

/**
 * Human-readable label for an application status.
 * @param {string} status - raw application.status value
 * @param {Function} [t] - optional i18next t function (dashboard namespace)
 */
export function getStatusLabel(status, t) {
  const entry = STATUS_LABELS[status];
  if (!entry) {
    // Unknown status — humanise the snake_case rather than rendering it raw
    if (status) return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return t ? t('status.inProgress', 'In Progress') : 'In Progress';
  }
  return t ? t(entry[0], entry[1]) : entry[1];
}

/** Quote-stage lead (report requested / quote sent, not yet paid). */
export function isQuoteStage(status) {
  return status === 'quote_requested' || status === 'quote_sent';
}

/** Has the application been paid for (any post-payment state)? */
export function isPaidApplication(app) {
  if (!app) return false;
  return (
    app.paymentStatus === 'paid' ||
    ['payment_completed', 'pending_attorney_filing', 'filing_in_progress', 'filed', 'registered', 'completed'].includes(app.status)
  );
}

/** Finished (registered/completed) application. */
export function isCompletedApplication(app) {
  if (!app) return false;
  return app.status === 'completed' || app.status === 'registered';
}

/** URL of the public guest wizard — new applications start there (quote-first funnel). */
export const MARKETING_WIZARD_URL = `${process.env.REACT_APP_MARKETING_URL || 'https://www.markotm.com'}/file-trademark`;
